import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { CommonApiProps } from '../types/common';
import { UserState } from '@gif/shared-global';

interface RootState {
  user: UserState;
}

interface GetProfileQueryProps extends CommonApiProps {
  accessToken: string;
}

// At least 1 of these fields needs to be present to update the profile
interface UpdateProfileQueryProps extends CommonApiProps {
  nickName?: string;
  birthYear?: number;
  birthMonth?: number;
  birthDay?: number;
  residencyStateProvince?: string | null;
  residencyCountry?: string;
  fanPlatformOptedIn?: boolean;
}

interface PostUpdateProfileResponse {
  message: boolean | string;
}

interface GetProfileResponse {
  login: string;
  email: string;
  firstName: string;
  lastName: string;
  nickName: string;
  secondEmail: string;
  birthDay: number;
  birthMonth: number;
  birthYear: number;
  commercialEmailsOptin: boolean;
  fanPlatformOptedIn: boolean;
  mobilePhone: string;
  residencyCountry: string;
  residencyStateProvince: string;
}

export const oktaApi = createApi({
  reducerPath: 'oktaApi',
  baseQuery: fetchBaseQuery({
    prepareHeaders: (headers, { getState }) => {
      const accessToken = (getState() as RootState).user.user?.accessToken
        ?.accessToken;
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getProfile: builder.query<GetProfileResponse, GetProfileQueryProps>({
      query: (args) => ({
        url: `${args.baseUrl}/api/v1/profile`,
        headers: {
          Authorization: `Bearer ${args.accessToken}`,
          ...args.headers,
        },
      }),
    }),
    updateProfile: builder.mutation<
      PostUpdateProfileResponse,
      UpdateProfileQueryProps
    >({
      query: (arg) => {
        const { baseUrl, headers, ...body } = arg;
        return {
          url: `${baseUrl}/api/v1/profile`,
          method: 'POST',
          headers,
          body: { ...body },
        };
      },
    }),
  }),
});

export const { useLazyGetProfileQuery, useUpdateProfileMutation } = oktaApi;
